import { DEFAULT_HEIGHT, DEFAULT_PERIOD, DEFAULT_WIDTH } from '../defaults';
import {
    sanitizeTextFieldValue,
    type DimensionsValue,
    type MarketSelectField,
    type UtmValue,
    type WidgetConfigState,
} from '../fields';
import type { EmbedConfig, WidgetSchema } from '../types';
import { parseDimension } from './cssDimension';

/**
 * Reduce a widget schema + the user's config state into the normalized
 * `EmbedConfig` consumed by both the embed-code generator (./buildEmbedCode) and
 * the live preview (../preview/WidgetPreview). Pure: no React, no `window`.
 *
 * Field keys are conventional and map straight onto EmbedConfig:
 *   event-search -> eventTicker, theme -> colorTheme, dimensions -> width/height,
 *   market-select(single) -> marketId, market-select(multi|orderable) -> marketIds,
 *   category-select -> category, color -> logoColor,
 *   boolean(showLogo|useMostRecentInSeries), enum(variant|period|seriesTicker), utm -> utm*.
 */
export function buildEmbedConfig(schema: WidgetSchema, config: WidgetConfigState): EmbedConfig {
    const embed: EmbedConfig = {
        eventTicker: '',
        widgetSize: schema.embed.fixed.widgetSize ?? 'large',
        variant: schema.embed.fixed.variant,
        colorTheme: 'light',
        width: DEFAULT_WIDTH,
        height: DEFAULT_HEIGHT,
        period: schema.embed.fixed.period ?? DEFAULT_PERIOD,
        iframeSrcPath: schema.embed.iframePath,
    };

    const valueOf = (key: string, fallback: unknown) => (config[key] !== undefined ? config[key] : fallback);

    for (const field of schema.fields) {
        const value = valueOf(field.key, field.defaultValue);

        switch (field.kind) {
            case 'event-search':
                embed.eventTicker = String(value ?? '');
                break;
            case 'theme':
                embed.colorTheme = value === 'dark' ? 'dark' : 'light';
                break;
            case 'dimensions': {
                const dims = (value ?? {}) as Partial<DimensionsValue>;
                const fixed = (field as { fixed?: Partial<DimensionsValue> }).fixed ?? {};
                // Validated here, once, so the live preview and the copied embed code size
                // themselves identically — the generator refuses anything it cannot safely
                // inline into a style attribute, and this is the only value it ever sees.
                const width = parseDimension(fixed.width || dims.width);
                const height = parseDimension(fixed.height || dims.height);
                if (width) embed.width = width;
                if (height) embed.height = height;
                break;
            }
            case 'market-select': {
                const ids = (Array.isArray(value) ? value : []) as string[];
                if ((field as MarketSelectField).mode === 'single') {
                    if (ids[0]) embed.marketId = ids[0];
                } else if (ids.length > 0) {
                    embed.marketIds = ids.join(',');
                }
                break;
            }
            case 'category-select':
                embed.category = String(value ?? '') || undefined;
                break;
            case 'color':
                embed.logoColor = String(value ?? '') || undefined;
                break;
            case 'boolean':
                if (field.key === 'showLogo') embed.showLogo = Boolean(value);
                else if (field.key === 'useMostRecentInSeries') embed.useMostRecentInSeries = Boolean(value);
                else if (field.key === 'showBorder') embed.showBorder = Boolean(value);
                else if (field.key === 'rounded') embed.rounded = Boolean(value);
                // Any other boolean is a widget-specific toggle (e.g. the midterms map's
                // forecast card); forward it explicitly so the iframe never guesses a default.
                else embed.iframeParams = { ...embed.iframeParams, [field.key]: String(Boolean(value)) };
                break;
            case 'text': {
                const text = sanitizeTextFieldValue(field, String(value ?? '').trim());
                if (text) embed.iframeParams = { ...embed.iframeParams, [field.key]: text };
                break;
            }
            case 'enum':
                if (field.key === 'variant') embed.variant = String(value ?? '') || embed.variant;
                else if (field.key === 'period') embed.period = String(value ?? '') || embed.period;
                else if (field.key === 'seriesTicker') embed.seriesTicker = String(value ?? '') || undefined;
                // The widget's route segment (e.g. the midterms map) rides the path, not the query.
                else if (field.key === schema.embed.iframePathParam && String(value ?? '')) {
                    embed.iframeSrcPath = `${schema.embed.iframePath ?? ''}/${encodeURIComponent(String(value))}`;
                }
                // Any other enum is a widget-specific choice (e.g. which GPU chip). Forward
                // it as a query param so direct-iframe widgets can expose a picker.
                else if (String(value ?? '')) {
                    embed.iframeParams = { ...embed.iframeParams, [field.key]: String(value) };
                }
                break;
            case 'utm': {
                const utm = (value ?? {}) as Partial<UtmValue>;
                if (utm.utmSource) embed.utmSource = utm.utmSource;
                if (utm.utmCampaign) embed.utmCampaign = utm.utmCampaign;
                if (utm.promoCode) embed.promoCode = utm.promoCode;
                break;
            }
        }
    }

    return embed;
}
